/**
 * 복잡한 HTML을 단순한 테이블 형태로 변환
 * aftersample.html -> beforesample.html 형태로 변환
 */

/**
 * 복잡한 HTML을 단순한 테이블로 변환
 * @param {string} htmlString - 복잡한 HTML (aftersample.html 형태)
 * @returns {string} - 단순한 HTML (beforesample.html 형태)
 */
export function simplifyHtmlTable(htmlString) {
  if (!htmlString) return '';

  let simplified = htmlString;

  simplified = simplified.replace(/<div[^>]*\bid\s*=\s*"hwpEditorBoardContent"[^>]*>[\s\S]*?<\/div>/gi, '');
  simplified = simplified.replace(/<div[^>]*\bdata-hjsonver\b[^>]*>[\s\S]*?<\/div>/gi, '');
  simplified = simplified.replace(/<!--\[data-hwpjson\][\s\S]*?-->/gi, '');
  simplified = simplified.replace(/<p class="0"><\/p>/gi, '');
  simplified = simplified.replace(/<p><\/p>/gi, '');

  // 1. </font-span> 같은 잘못된 닫기 태그 제거
  simplified = simplified.replace(/<\/font-span>/gi, '');
  
  // 2. <font-span> 같은 잘못된 여는 태그 제거
  simplified = simplified.replace(/<font-span[^>]*>/gi, '');

  // 2-b. <b>/<strong> 의 워드(MS Word) 전용 스타일 속성 제거 (예: style="mso-bidi-font-weight:normal")
  //      정상 페이로드(한글/엑셀)는 속성 없는 <b>를 사용하므로 동일하게 맞춘다.
  simplified = simplified.replace(/<b\b[^>]*>/gi, '<b>');
  simplified = simplified.replace(/<strong\b[^>]*>/gi, '<strong>');

  // 2-c. 다운레벨 노출형 조건부 주석 제거 (<!--[if !supportEmptyParas]-->...<!--[endif]-->, <!--[if !vml]-->... 등)
  //      정상 페이로드에는 이 주석이 남지 않는다.
  simplified = simplified.replace(/<!--\[if[^\]]*\]-->[\s\S]*?<!--\[endif\]-->/gi, '');

  // 3. <td> 내부 정리.
  //    - 워드(MS Word) 클립보드: <p align="center">로 컬럼 정렬을 표현하므로 단락을 보존해야
  //      df_cellMerge가 가운데정렬(헤더) 컬럼을 인식한다.
  //    - 한글/엑셀 클립보드: 정렬 없는 <p>를 줄마다 사용하므로 <p>를 <br>로 합쳐야 정상 인식된다.
  //    문서 전체에 가운데정렬 <p>가 하나라도 있으면 워드 양식으로 판단한다.
  const hasCenterAlignedParagraph = /<p\b[^>]*\balign\s*=\s*["']?\s*center/i.test(simplified);

  simplified = simplified.replace(/<td([^>]*)>([\s\S]*?)<\/td>/gi, (match, attrs, content) => {
    let cleaned = content;

    if (hasCenterAlignedParagraph) {
      // 워드: <p> 단락 보존 (align="center"만 유지, mso/class/style 등은 제거)
      cleaned = cleaned.replace(/<p\b([^>]*)>/gi, (mm, pAttrs) => {
        const isCenter = /align\s*=\s*["']?\s*center/i.test(pAttrs || '');
        return isCenter ? '<p align="center">' : '<p>';
      });
    } else {
      // 한글/엑셀: <p> 단락을 <br>로 합침
      cleaned = cleaned
        .replace(/<p[^>]*>\s*/gi, '')   // <p> 여는 태그 제거 (뒤 공백 포함)
        .replace(/\s*<\/p>/gi, '<br>')  // </p>를 <br>로 변환 (앞 공백 제거)
        .replace(/<br>\s*$/i, '');      // 마지막 <br> 제거
    }

    // 줄바꿈을 공백으로 변환하고 연속 공백 정리 (단, <br> 태그는 유지)
    cleaned = cleaned.replace(/\n/g, ' ').replace(/  +/g, ' ');

    // <br> 뒤의 공백 제거
    cleaned = cleaned.replace(/<br>\s+/gi, '<br>');

    return `<td${attrs}>${cleaned}</td>`;
  });

  // 4. 이미지 태그 제거 (<!--[if !vml]-->...<!--[endif]-->)
  simplified = simplified.replace(/<!--\[if !vml\]-->[\s\S]*?<!--\[endif\]-->/gi, '');

  // 5. MS Office 조건부 주석 제거
  simplified = simplified.replace(/<!--\[if[^\]]*\]>[\s\S]*?<!\[endif\]-->/gi, '');

  // 6. <o:p> 태그는 유지 (beforesample.html에 존재)
  // simplified = simplified.replace(/<o:p[^>]*>[\s\S]*?<\/o:p>/gi, '');
  // simplified = simplified.replace(/<o:p[^>]*\/>/gi, '');

  // 7. <br> 태그는 유지 (beforesample.html에 존재)
  // simplified = simplified.replace(/<br\s*\/?>/gi, ' ');

  // 8. <td> 태그 정리
  simplified = simplified.replace(/\s+<\/td>/gi, '</td>');

  simplified = simplified.replace(/<td([^>]*)>/gi, (match, attrs) => {
    const colspanMatch = attrs.match(/colspan\s*=\s*["']?(\d+)["']?/i);
    const rowspanMatch = attrs.match(/rowspan\s*=\s*["']?(\d+)["']?/i);
    let newAttrs = '';
    if (colspanMatch) newAttrs += ` colspan="${colspanMatch[1]}"`;
    if (rowspanMatch) newAttrs += ` rowspan="${rowspanMatch[1]}"`;
    return `<td${newAttrs}>`;
  });

  // 9. <tr> 태그 공백 정리
  simplified = simplified.replace(/<tr[^>]*>/gi, '<tr>'); // <tr> 속성 제거
  simplified = simplified.replace(/\s*<\/tr>/gi, '\n        </tr>'); // 8 spaces before </tr>
  
  // 10. <td> 태그 들여쓰기 (tr 정리 후)
  simplified = simplified.replace(/<tr>\s*/gi, '<tr>\n            '); // 12 spaces before first <td>
  simplified = simplified.replace(/<\/td>\s*<td/gi, '</td>\n            <td'); // 12 spaces before subsequent <td>

  // 11. <tbody> 태그 정리
  simplified = simplified.replace(/<tbody[^>]*>/gi, '<tbody>'); // <tbody> 속성 제거
  simplified = simplified.replace(/<tbody>\s*/gi, '<tbody>\n        '); // 8 spaces
  simplified = simplified.replace(/\s*<\/tbody>/gi, '\n    </tbody>'); // 4 spaces

  // 12. <table> 태그 정리
  simplified = simplified.replace(/<table[^>]*>/gi, '<table>'); // <table> 속성 제거
  simplified = simplified.replace(/<table>\s*/gi, '<table>\n    '); // 4 spaces
  simplified = simplified.replace(/\s*<\/table>/gi, '\n</table>'); // no spaces

  // 13. 연속된 줄바꿈을 하나로
  simplified = simplified.replace(/\n{3,}/g, '\n\n');

  // 14. 앞뒤 공백 제거
  simplified = simplified.trim();

  // 15. </div> 제거 (시작 부분에 있을 수 있음)
  simplified = simplified.replace(/^<\/div>\s*/i, '');

  // 16. &nbsp; 를 공백으로
  simplified = simplified.replace(/&nbsp;/g, ' ');

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(String(simplified || ''), 'text/html');
    const tables = Array.from(doc.querySelectorAll('table'));
    if (tables.length > 0) {
      const base = tables[0];
      const baseTbody = base.querySelector('tbody') || (() => {
        const tb = doc.createElement('tbody');
        base.appendChild(tb);
        return tb;
      })();
      for (let i = 1; i < tables.length; i++) {
        const t = tables[i];
        const rows = Array.from((t.querySelector('tbody') || t).querySelectorAll('tr'));
        rows.forEach((tr) => baseTbody.appendChild(tr));
        t.remove();
      }
      simplified = String(base.outerHTML || '').trim();
    }
  } catch {
  }

  return simplified;
}

/**
 * API 페이로드 생성 (beforesample.html 형태)
 * @param {string} htmlContent - 복잡한 HTML
 * @param {object} additionalParams - 추가 파라미터
 * @returns {object} - API 페이로드
 */
export function createSimplifiedPayload(htmlContent, additionalParams = {}) {
  const simplifiedHtml = simplifyHtmlTable(htmlContent);
  
  return {
    html내용: simplifiedHtml,
    ...additionalParams
  };
}

/**
 * 디버그용: 변환 전후 비교
 * @param {string} htmlString - 원본 HTML
 */
export function debugSimplification(htmlString) {
  const simplified = simplifyHtmlTable(htmlString);
  return simplified;
}

export default {
  simplifyHtmlTable,
  createSimplifiedPayload,
  debugSimplification
};
